/**
 * PerformanceAthleteCard (iter74)
 *
 * The rebuilt "Athlete Card" replacing the Personal Pathway Pyramid on
 * /progress (Advanced view) and /dashboard. High-end athletic aesthetic:
 *
 *   ┌ 3 large circular progress rings ─ Foundation / Tactical / Advanced
 *   ├ Skill grid  ─ individual axes with subtle progress bars
 *   └ Milestone / focus tier callout
 *
 * All colours reference CSS variables (--surface, --border, --volt, --azure)
 * so the card flips between the dark "Elite Performance" and light
 * "Clean Professional" themes without a re-render.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Loader, TrendingUp } from "lucide-react";
import "./PerformanceAthleteCard.css";

// Same tier groupings as the retired Pyramid (user 2a).
const TIERS = [
  {
    key: "foundation",
    name: "Foundation",
    axes: ["Serve", "Return", "Volley", "Fitness"],
    target: 3.0,
    swatch: "var(--azure)",
    subhead: "Fundamentals that anchor every rally.",
  },
  {
    key: "tactical",
    name: "Tactical",
    axes: ["Baseline", "Reset", "Footwork", "Mental"],
    target: 3.5,
    swatch: "var(--volt)",
    subhead: "Positioning + shot selection.",
  },
  {
    key: "advanced",
    name: "Advanced",
    axes: ["Dink", "Third Shot Drop", "Kitchen Play"],
    target: 4.0,
    swatch: "var(--orange)",
    subhead: "Winning the kitchen battle.",
  },
];

const LEVEL_FLOOR = 2.5;

function computeTier(snapshot, tier) {
  const axes = snapshot?.current?.axes || {};
  const values = tier.axes
    .map((a) => ({ axis: a, level: axes[a] }))
    .filter((r) => typeof r.level === "number");
  const hasData = values.length > 0;
  const mean = hasData ? values.reduce((s, v) => s + v.level, 0) / values.length : 0;
  const denom = Math.max(0.01, tier.target - LEVEL_FLOOR);
  const fill = hasData ? Math.max(0, Math.min(100, ((mean - LEVEL_FLOOR) / denom) * 100)) : 0;
  const gap = hasData ? Math.max(0, tier.target - mean) : (tier.target - LEVEL_FLOOR);
  const perSkill = tier.axes.map((a) => {
    const lv = axes[a];
    if (typeof lv !== "number") return { axis: a, level: null, fill: 0 };
    const pct = Math.max(0, Math.min(100, ((lv - LEVEL_FLOOR) / denom) * 100));
    return { axis: a, level: lv, fill: pct };
  });
  return { ...tier, hasData, mean, fill, gap, perSkill };
}

export default function PerformanceAthleteCard({ studentId, sport, compact = false }) {
  const [snapshot, setSnapshot] = useState(null);
  const [milestone, setMilestone] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get(`/players/${studentId}/skill-snapshot`).catch(() => ({ data: null })),
      api.get(`/students/${studentId}/milestones/active`, { params: { sport } }).catch(() => ({ data: { active: null } })),
    ]).then(([snap, ms]) => {
      if (cancelled) return;
      setSnapshot(snap.data);
      setMilestone(ms.data?.active || null);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [studentId, sport]);

  const tiers = useMemo(() => TIERS.map((t) => computeTier(snapshot, t)), [snapshot]);
  const focusTier = useMemo(() => {
    const withData = tiers.filter((t) => t.hasData);
    if (!withData.length) return tiers[0];
    return withData.slice().sort((a, b) => b.gap - a.gap)[0];
  }, [tiers]);

  if (loading) {
    return (
      <div className="athlete-card athlete-card--loading" data-testid="athlete-card-loading">
        <Loader className="w-6 h-6 animate-spin" style={{ color: "var(--volt)" }} />
        <p>Loading your athlete card…</p>
      </div>
    );
  }

  return (
    <div className={"athlete-card" + (compact ? " athlete-card--compact" : "")} data-testid="performance-athlete-card">
      {/* Header */}
      <div className="athlete-card__header">
        <div>
          <div className="athlete-card__eyebrow">PERFORMANCE ATHLETE CARD</div>
          <h2 className="athlete-card__title">Your development tiers</h2>
        </div>
        {focusTier?.hasData && (
          <div className="athlete-card__focus" data-testid="athlete-card-focus">
            <TrendingUp className="w-3.5 h-3.5" />
            <span>Focus tier</span>
            <strong>{focusTier.name}</strong>
          </div>
        )}
      </div>

      {/* 3 progress rings */}
      <div className="athlete-card__rings">
        {tiers.map((t) => (
          <TierRing key={t.key} tier={t} />
        ))}
      </div>

      {/* Skill grid — full skills list with progress bars */}
      <div className="athlete-card__grid">
        {tiers.map((t) => (
          <div key={t.key} className="athlete-card__group" data-testid={`athlete-card-group-${t.key}`}>
            <div className="athlete-card__group-head">
              <span className="dot" style={{ background: t.swatch }} />
              <span className="name">{t.name}</span>
              <span className="tgt">→ target {t.target.toFixed(1)}</span>
            </div>
            <ul className="athlete-card__skills">
              {t.perSkill.map((s) => (
                <li key={s.axis} className="athlete-card__skill" data-testid={`athlete-card-skill-${s.axis}`}>
                  <span className="skill-name">{s.axis}</span>
                  <span className="skill-bar" aria-hidden>
                    <span className="skill-bar__fill" style={{ width: `${s.fill}%`, background: t.swatch }} />
                  </span>
                  <span className="skill-val">
                    {s.level == null ? <em>—</em> : s.level.toFixed(1)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Milestone strip */}
      {milestone && (
        <div className="athlete-card__milestone" data-testid="athlete-card-milestone">
          <div className="athlete-card__milestone-eyebrow">ACTIVE MILESTONE</div>
          <div className="athlete-card__milestone-name">{milestone.name}</div>
          <div className="athlete-card__milestone-bar">
            <span style={{ width: `${milestone.progress?.aggregate ?? 0}%`, background: "var(--volt)" }} />
          </div>
          <div className="athlete-card__milestone-pct">{milestone.progress?.aggregate ?? 0}% complete</div>
        </div>
      )}
    </div>
  );
}

function TierRing({ tier }) {
  // Iter76: thin stroke (2.5) so the rings read like a high-end fitness
  // tracker (Whoop / Oura) rather than a chunky dashboard donut.
  const size = 132;
  const stroke = 2.5;
  const r = (size - stroke) / 2 - 4; // small breathing gap between track + glow
  const c = 2 * Math.PI * r;
  const dash = (tier.fill / 100) * c;
  return (
    <div className="tier-ring" data-testid={`athlete-card-ring-${tier.key}`}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="tier-ring__svg">
        {/* Faint outer glow so the ring pops in dark mode without adding bulk */}
        <circle
          cx={size / 2} cy={size / 2} r={r + 3}
          fill="none"
          stroke={tier.swatch}
          strokeWidth="0.6"
          opacity="0.35"
        />
        {/* Track */}
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none"
          stroke="var(--border)"
          strokeWidth={stroke}
        />
        {/* Progress arc */}
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none"
          stroke={tier.swatch}
          strokeWidth={stroke}
          strokeDasharray={`${dash} ${c}`}
          strokeDashoffset={c * 0.25}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: "stroke-dasharray 0.6s ease-out" }}
        />
        {/* Cap dot at the end of the progress arc — like Apple Fitness rings */}
        {tier.fill > 0 && <ProgressCap fill={tier.fill} r={r} size={size} color={tier.swatch} />}
      </svg>
      <div className="tier-ring__center">
        <div className="tier-ring__pct">{Math.round(tier.fill)}%</div>
        <div className="tier-ring__mean">
          {tier.hasData ? `avg ${tier.mean.toFixed(2)}` : "no data yet"}
        </div>
      </div>
      <div className="tier-ring__label">
        <div className="tier-ring__name">{tier.name}</div>
        <div className="tier-ring__sub">{tier.subhead}</div>
      </div>
    </div>
  );
}

// Small filled circle that sits at the tip of the progress arc so the ring
// reads like an Apple Fitness / Oura-style tracker rather than a plain donut.
function ProgressCap({ fill, r, size, color }) {
  const angle = (fill / 100) * 2 * Math.PI - Math.PI / 2;
  const cx = size / 2 + r * Math.cos(angle);
  const cy = size / 2 + r * Math.sin(angle);
  return <circle cx={cx} cy={cy} r={3} fill={color} />;
}
